import { SelectDropdown, InputField } from "@sentara-health/optimahealth_web";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormError, GlobalError } from "../../organisms/form";
import { useEffect } from "react";
import {
  AlphaNumericAsterisks,
  NameRegex,
  SSNRegex,
  SubscriberIDV2Regex,
} from "utils/constants/Regex";
import { NameChange, ssnOncheck, customSort } from "utils/helpers/HelperFn";
import { useAuth } from "providers";
import { subscriberMinLength } from "utils/constants/Constants";
import { useNavigate } from "react-router";
import { REQUEST_MEMBER_ID_CARD } from "routes";
import RequestMemberIDCardSubmit from "./RequestMemberIDCardSubmit";
import { useState } from "react";
import { useFeatureFlag } from "../../providers/featureFlag/FeatureFlagProvider";
import { FeatureFlagKey } from "../../providers/featureFlag/FeatureFlagProvider.types";

interface RequestMemberIDCardFormProps {
  setError: Function;
}

function RequestMemberIDCardForm({ setError }: RequestMemberIDCardFormProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const accountNumberField: boolean = useFeatureFlag(
    FeatureFlagKey.AccountNumberField
  );
  const isAccountNumberFieldFlagEnabled: boolean = useFeatureFlag(
    FeatureFlagKey.AccountNumberField
  );
  const {
    register,
    reset,
    trigger,
    formState,
    getValues,
    resetField,
    setFocus,
    watch,
    setValue,
  } = useFormContext();

  // Watch for changes in accountId dropdown
  const selectedAccountId = watch("accountId");

  // Get unique accounts
  const uniqueAccounts = Array.from(
    new Map(
      user?.groups?.map((item) => [
        `${item.accountId}|${item.accountName}`,
        item,
      ])
    ).values()
    ).sort((a, b) => {
      const byName = customSort(a, b, "accountName");
      if (byName !== 0) return byName;
      return customSort(a, b, "accountId");
    });

  // Filter groups based on selected account
  const filteredGroups = selectedAccountId
    ? user?.groups?.filter((group) => group.accountId === selectedAccountId) ||
      []
    : user?.groups || [];

  // Set default account on first load
  useEffect(() => {
    if (
      isAccountNumberFieldFlagEnabled &&
      uniqueAccounts.length > 0 &&
      !selectedAccountId
    ) {
      const firstAccountId = uniqueAccounts[0]?.accountId || "";
      setValue("accountId", firstAccountId);
    }
  }, [
    isAccountNumberFieldFlagEnabled,
    uniqueAccounts,
    selectedAccountId,
    setValue,
  ]);

  // Handle account dropdown change
  const handleAccountChange = () => {
    resetField("groupId"); // Clear group selection when account changes
    trigger("searchInput");
    setError(false);
  };

  //For resetting the form
  const handleClearForm = () => {
    // Get the first account ID if available
    const firstAccountId = uniqueAccounts[0]?.accountId || "";

    reset({
      groupId: "",
      memberId: "",
      subscriberSecurityNumber: "",
      firstName: "",
      lastName: "",
      checkboxAllMembers: "",
      accountId: accountNumberField ? firstAccountId : "", // Set to first account if enabled
    });
    setError(false);
    navigate(`/app/${REQUEST_MEMBER_ID_CARD}`);
  };

  const validateFirstName = () => {
    if (getValues("firstName") && getValues("lastName") === "") {
      return t("requestMemberIDCard.validationLastname");
    }
  };

  register("lastName", { validate: validateFirstName });

  const searachByValdate = () => {
    if (
      !getValues("memberId") &&
      !getValues("subscriberSecurityNumber") &&
      !getValues("lastName") &&
      !getValues("checkboxAllMembers")
    ) {
      return t("requestMemberIDCard.atleastOneCriteria");
    }
  };

  register("searchInput", { validate: searachByValdate });

  return (
    <div>
      <div className="row">
        <div className="col-lg-8 col-md-8 col-sm-12 col-12">
          <div className="mb-3">
            <GlobalError name="searchInput" tabIndex={-1} />
          </div>
          {accountNumberField && (
            <div className="mb-4">
              <label
                className="pb-1"
                htmlFor="order_id_account_id"
                data-testid="lblOrderMemberIdCardAccountId"
              >
                {t("requestMemberIDCard.selectAccountNum")}
              </label>
              <div>
                <SelectDropdown
                  {...register("accountId", {
                    onChange: handleAccountChange,
                  })}
                  className={
                    formState.errors.accountId?.message
                      ? "form-select error-input-bdr"
                      : "form-select"
                  }
                  dataTestId="optOrderMemberIdCardAccountList"
                  id="order_id_account_id"
                  aria-describedby="order_id_account_id_error"
                >
                  {uniqueAccounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.accountId} {account.accountName}
                    </option>
                  ))}
                </SelectDropdown>
                <div
                  className={
                    formState.errors.accountId?.message ? "" : "d-none"
                  }
                >
                  <div
                    id="order_id_account_id_error"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <FormError name="accountId" />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="mb-4">
            <label
              className="pb-1"
              htmlFor="order_id_group_id"
              data-testid="lblOrderMemberIdCard"
            >
              {t("requestMemberIDCard.selectGroup")}
            </label>
            <div>
              <SelectDropdown
                {...register("groupId", {
                  onChange: () => {
                    trigger("searchInput");
                  },
                })}
                className={
                  formState.errors.groupId?.message
                    ? "form-select error-input-bdr"
                    : "form-select"
                }
                dataTestId="optOrderMemberIdCardGroupList"
                id="order_id_group_id"
                aria-describedby="order_id_group_id_error"
              >
                <option value="">{t("requestMemberIDCard.allgroups")}</option>
                {(isAccountNumberFieldFlagEnabled
                  ? filteredGroups
                  : user?.groups || []
                )
                  ?.slice()
                  .sort((a, b) => {
                    const byName = customSort(a, b, "groupName");
                    if (byName !== 0) return byName;
                    return customSort(a, b, "groupID");
                  })
                  .map((item: { groupID: string; groupName: string }) => {
                  const trimmedGroupID = item.groupID?.toString().trim();
                  return (
                    <option key={trimmedGroupID} value={trimmedGroupID}>
                      {trimmedGroupID} {item.groupName}
                    </option>
                  );
                })}
              </SelectDropdown>
              <div
                className={formState.errors.groupId?.message ? "" : "d-none"}
              >
                <div
                  id="order_id_group_id_error"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormError name="groupId" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label
              className="pb-1"
              htmlFor="order_id_member_id"
              data-testid="lblOrderMemberIdCardSubscriberId"
            >
              {t("requestMemberIDCard.memberIDNumber")}
            </label>
            <div>
              <InputField
                maxLength={20}
                pattern={AlphaNumericAsterisks}
                {...register("memberId", {
                  minLength: {
                    value: subscriberMinLength,
                    message: t("requestMemberIDCard.minimumMemberID"),
                  },

                  onChange: (e) => {
                    trigger("searchInput");
                    resetField("subscriberSecurityNumber");
                    resetField("firstName");
                    resetField("lastName");

                    setError(false);
                  },
                  pattern: {
                    value: SubscriberIDV2Regex,
                    message: t("requestMemberIDCard.validationSubscriberID"),
                  },
                })}
                className={
                  formState.errors.memberId?.message
                    ? "form-input error-input-bdr"
                    : "form-input"
                }
                placeholder={t("enrollmentHistoryForm.plcHolderMemberId")}
                dataTestId="inputOrderMemberIdCardSubscriberId"
                id="order_id_member_id"
                aria-describedby="order_id_member_id_error"
              />
              <div
                className={formState.errors.memberId?.message ? "" : "d-none"}
              >
                <div
                  id="order_id_security_no_error"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormError name="memberId" />
                </div>
              </div>
              <p className="granite-gray-color pt-1">
                <small>{t("enrollmentHistoryForm.includeAsterisk")}</small>
              </p>
            </div>
          </div>

          <div className="pos-rel pt-2">
            <hr className="m-0" />
            <strong className="or-separator">
              {t("requestMemberIDCard.or")}
            </strong>
          </div>

          <div className="mb-4">
            <label
              className="pb-1"
              htmlFor="order_id_security_no"
              data-testid="lblOrderMemberIdCardSubscriberSSN"
            >
              {t("requestMemberIDCard.sssn")}
            </label>
            <div>
              <InputField
                maxLength={9}
                type="text"
                {...register("subscriberSecurityNumber", {
                  setValueAs: (v) => ssnOncheck(v),
                  onChange: (e) => {
                    e.target.value = ssnOncheck(e.target.value);
                    trigger("searchInput");
                    resetField("memberId");
                    resetField("firstName");
                    resetField("lastName");
                    setError(false);
                  },
                  pattern: {
                    value: SSNRegex,
                    message: t("requestMemberIDCard.validationSSN"),
                  },
                })}
                forceNumber={true}
                className={
                  formState.errors.subscriberSecurityNumber?.message
                    ? "form-input error-input-bdr"
                    : "form-input"
                }
                placeholder={t("requestMemberIDCard.plcHolderSSN")}
                dataTestId="inputOrderMemberIdCardSubscriberSSN"
                id="order_id_security_no"
                aria-describedby="order_id_security_no_error"
              />
              <div
                className={
                  formState.errors.subscriberSecurityNumber?.message
                    ? ""
                    : "d-none"
                }
              >
                <div
                  id="order_id_security_no_error"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormError name="subscriberSecurityNumber" />
                </div>
              </div>
            </div>
          </div>

          <div className="pos-rel pt-2">
            <hr className="m-0" />
            <strong className="or-separator">
              {t("requestMemberIDCard.or")}
            </strong>
          </div>

          <div className="mb-4">
            <label
              className="pb-1"
              htmlFor="order_id_last_name"
              data-testid="lblOrderMemberIdCardSubscriberLastName"
            >
              {t("requestMemberIDCard.memberLastName")}
            </label>
            <div>
              <InputField
                pattern={NameRegex}
                maxLength={20}
                {...register("lastName", {
                  onChange: (e) => {
                    NameChange(e);
                    trigger("lastName");
                    trigger("searchInput");
                    resetField("memberId");
                    resetField("subscriberSecurityNumber");
                    setError(false);
                  },
                })}
                className={
                  formState.errors.lastName?.message
                    ? "form-input error-input-bdr"
                    : "form-input"
                }
                placeholder={t("requestMemberIDCard.plcLastName")}
                dataTestId="inputOrderMemberIdCardSubscriberLastName"
                id="order_id_last_name"
                aria-describedby="order_id_last_name_error"
              />
              <div
                className={formState.errors.lastName?.message ? "" : "d-none"}
              >
                <div
                  id="order_id_last_name_error"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormError name="lastName" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label
              className="pb-1"
              htmlFor="order_id_first_name"
              data-testid="lblOrderMemberIdCardSubscriberFirstName"
            >
              {t("requestMemberIDCard.memberFirstName")}
            </label>
            <div>
              <InputField
                pattern={NameRegex}
                maxLength={20}
                type="text"
                forceChar={true}
                {...register("firstName", {
                  onChange: (e) => {
                    NameChange(e);
                    trigger("lastName");
                    resetField("memberId");
                    resetField("subscriberSecurityNumber");
                    setError(false);
                  },
                })}
                className={
                  formState.errors.firstName?.message
                    ? "form-input error-input-bdr"
                    : "form-input"
                }
                placeholder={t("requestMemberIDCard.plcFirstName")}
                dataTestId="inputOrderMemberIdCardSubscriberFirstName"
                id="order_id_first_name"
                aria-describedby="order_id_first_name_error"
              />
              <div
                className={formState.errors.firstName?.message ? "" : "d-none"}
              >
                <div
                  id="order_id_first_name_error"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormError name="firstName" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="d-flex align-items-center mb-2">
          <InputField
            className="form-check-input mt-0 p-0"
            type="checkbox"
            id={"checkboxid"}
            {...register("checkboxAllMembers", {
              onChange: (e) => {
                trigger("searchInput");
              },
            })}
            data-testid={"chkOrderMemberIdCard100orLess"}
          />
          <label
            className="check-label-text check-label-pad"
            htmlFor={"checkboxid"}
          >
            {t("requestMemberIDCard.note")}
          </label>
        </div>
      </div>
      <hr className="mt-0 mb-4" />
      <RequestMemberIDCardSubmit
        handleClearForm={handleClearForm}
        formState={formState}
      />
    </div>
  );
}

export default RequestMemberIDCardForm;
